import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  VariableList,
  PeopleList,
  TranscriptMockData,
  AICallSummary,
} from '../../MockDataMessages'
import { normalizeAiFeatureKey, postAiCompletionApi } from '@services/api'
import { ChatPreviewHooks } from '@dataTypes/message.types'
import { CHAT_HEADING_BUTTONS, CHAT_LEFT_BUTTONS } from '@constants/Message'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'
import moment from 'moment'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import {
  ActionBeginEventArgs,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-richtexteditor'
import {
  ClickEventArgs,
  MenuEventArgs,
} from '@syncfusion/ej2-react-navigations'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  createChat,
  updateChat,
  createSms,
  updateConversation,
  addContactGroupParticipant,
  unblockUser,
  bulkUpdateChatMessages,
  fetchMessagingSettingsByClinic,
} from '@store/actions'
import { extractTextfromHTML } from '@utils/messageFormatter.util'
import {
  ChatChannel,
  ChatDirection,
  IChatConversation,
  IChatMessage,
  IChatParticipant,
  SendStatus,
} from '@dataTypes/chat.type'
import { IEditorInput } from '@components/common/TextEditor/useTextEditor'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import {
  SET_CONTACT_LIST,
  SET_CHANNEL,
  SET_CHAT_LIST,
  SET_INPUT_NUMBER,
  SET_PARTICIPANT,
  SET_SHOW_MODAL,
  SET_CONVERSATION_FOR_NEW_GROUP,
} from '@store/reducers/messageCenter.reducer'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import { showCommunicationLogNotFoundToast } from '@utils/patientProfileCommunicationNavigation.util'

function useChatPreview(): ChatPreviewHooks {
  const { showToast } = useNotificationToast()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const messagingSettings = useAppSelector(
    (state) => state.messagingSettings.current,
  )
  const blockedUsers = useAppSelector((state) => state.blockedUser.data)
  const conversations = useAppSelector((state) => state.chat.data)
  const chatLoading = useAppSelector((state) => state.chat.loading)
  const selectedMessageState = useAppSelector(
    (state) => state.chat.selectedMessage,
  )
  const callerInfo = useAppSelector((state) => state.calls.callerInfo)
  const currentCall = useAppSelector((state) => state.calls.currentCall)
  const device = useAppSelector((state) => state.calls.device)
  const contactGroups = useAppSelector((state) => state.contactGroup.data)
  // use states
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [aiCallSummary] = useState<string>(AICallSummary)
  const [chatInput, setChatInput] = useState<string>('')
  const [editorInput, setEditorInput] = useState<IEditorInput>({
    text: '',
    htmlContent: '',
  })
  const [isAiEnabled, setIsAiEnabled] = useState<boolean>(false)
  const [isBlocked, setIsBlocked] = useState<boolean>(false)
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isKebabBtnActive, setIsKebabBtnActive] = useState<boolean>(false)
  const [inputPrompt, setInputPrompt] = useState<string>('')
  const [lastActiveIndex, setLastActiveIndex] = useState<number>(-1)
  const [showDefaultToolbar, setShowDefaultToolbar] = useState<boolean>(true)
  const [showSummary, setShowSummary] = useState<Record<string, boolean>>({})
  const [showTranscriptModal, setShowTranscriptModal] = useState<
    Record<string, boolean>
  >({})
  const [showConfirmUnblockModal, setShowConfirmUnblockModal] =
    useState<boolean>(false)
  const [messageData, setMessageData] = useState<IChatConversation[]>(
    conversations || [],
  )
  const [toastText, setToastText] = useState<string | null>(null)
  const [chat, setChat] = useState<IChatMessage[] | null>(null)
  const [participant, setParticipant] = useState<IChatParticipant[] | null>(
    null,
  )
  const [pinnedChat, setPinnedChat] = useState<IChatMessage | null>(null)
  const [lastReadMessageIndex, setLastReadMessageIndex] = useState<number>(-1)
  const [aiAction, setAiAction] = useState<string>('custom')
  const [aiResponse, setAiResponse] = useState<string>('')
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  // use ref
  const mentionRef = useRef<MentionComponent>(null)
  const callerInfoRef = useRef(callerInfo)
  const currentCallRef = useRef(currentCall)
  const conversationRef = useRef(conversations)
  const variableRef = useRef<MentionComponent>(null)
  const toolbarRef = useRef<RichTextEditorComponent>(null)
  const chatRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const appliedDeepLinkChatMessageIdRef = useRef<string | null>(null)
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null)
  const pinnedChatRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  // constants
  const defaultToolbarSettings: ToolbarSettingsModel = {
    items: [],
    enable: true,
  }
  const peopleList: string[] = []
  PeopleList.map((data) => {
    peopleList.push(data.name)
  })

  const handleConfirmEditChat = async (): Promise<void> => {
    if (!chat || !selectedMessageState?.channel) return
    const _chat = chat[lastActiveIndex] as IChatMessage
    const newHtmlContent = editorInput.htmlContent
    const newText = editorInput.text

    if (
      lastActiveIndex !== -1 &&
      (newText !== _chat.content || newHtmlContent !== _chat.html_content) &&
      chat[lastActiveIndex].chat_message_id
    ) {
      setIsEditing(false)
      setEditorInput({ text: '', htmlContent: '' })
      try {
        await dispatch(
          updateChat({
            chat_message_id: chat[lastActiveIndex].chat_message_id!,
            data: {
              content: newText,
              html_content: newHtmlContent,
              is_edited: true,
            },
          }),
        ).unwrap()
        showToast(
          createSuccessToast('Message updated', 'Your edit has been saved.'),
        )
      } catch {
        showToast(
          createFailedToast(
            'Update failed',
            'Failed to update message. Please try again.',
          ),
        )
      }
    }
  }

  const makeCall = async (): Promise<void> => {
    if (
      device &&
      participant &&
      participant.length === 1 &&
      participant[0].chat_participant_id &&
      conversations
    ) {
      const contact = participant[0].phone_number
      if (contact) {
        dispatch(SET_INPUT_NUMBER(contact))
      }
      dispatch(setActiveSidePanel('phone'))
    }
  }

  const handleUnblockUser = async (): Promise<void> => {
    if (!participant?.[0]?.contact?.contact_id) return
    const blockedUserId = blockedUsers.find(
      (blockedUser) =>
        blockedUser.contact_id === participant?.[0]?.contact?.contact_id,
    )?.blocked_user_id
    if (!blockedUserId) return
    try {
      await dispatch(unblockUser(blockedUserId)).unwrap()
      showToast(
        createSuccessToast(
          'User unblocked',
          'The contact has been unblocked and can message you again.',
        ),
      )
      setShowConfirmUnblockModal(false)
    } catch {
      showToast(
        createFailedToast(
          'Unblock failed',
          'Failed to unblock user. Please try again.',
        ),
      )
    }
  }

  const handleMoreOptionsDropdown = async (
    args: MenuEventArgs,
  ): Promise<void> => {
    const action = args.item.id
    if (action && selectedMessageState?.chat_conversation_id) {
      switch (action.toLowerCase()) {
        case 'archive':
          try {
            await dispatch(
              updateConversation({
                id: selectedMessageState.chat_conversation_id,
                data: {
                  is_archived: true,
                  date_time_archived_at: new Date(),
                },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat archived',
                'Conversation has been archived.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Archive failed',
                'Archiving conversation failed. Please try again.',
              ),
            )
          }
          break
        case 'unarchive':
          try {
            await dispatch(
              updateConversation({
                id: selectedMessageState.chat_conversation_id,
                data: { is_archived: false, date_time_archived_at: null },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat unarchived',
                'Conversation has been restored from archive.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Unarchive failed',
                'Restoring conversation failed. Please try again.',
              ),
            )
          }
          break
        case 'pin':
          {
            try {
              await dispatch(
                updateConversation({
                  id: selectedMessageState.chat_conversation_id,
                  data: { is_pinned: true },
                }),
              ).unwrap()
              showToast(
                createSuccessToast('Chat pinned', 'Conversation pinned.'),
              )
            } catch {
              showToast(
                createFailedToast(
                  'Pinning failed',
                  'Pinning chat failed. Please try again.',
                ),
              )
            }
          }
          break
        case 'unpin':
          {
            try {
              await dispatch(
                updateConversation({
                  id: selectedMessageState.chat_conversation_id,
                  data: { is_pinned: false },
                }),
              ).unwrap()
              showToast(
                createSuccessToast('Chat unpinned', 'Conversation unpinned.'),
              )
            } catch {
              showToast(
                createFailedToast(
                  'Unpinning failed',
                  'Unpinning chat failed. Please try again.',
                ),
              )
            }
          }
          break
        case 'group':
          break
        case 'create_group':
          {
            if (
              selectedMessageState?.conversation_kind === 'group' &&
              selectedMessageState?.chat_conversation_id
            ) {
              dispatch(
                SET_CONVERSATION_FOR_NEW_GROUP(
                  selectedMessageState?.chat_conversation_id,
                ),
              )
            }
            dispatch(
              SET_CONTACT_LIST(
                (participant ?? [])
                  .map((p) => p.contact)
                  .filter((c): c is NonNullable<typeof c> => c !== undefined),
              ),
            )
            if (selectedMessageState?.channel)
              dispatch(SET_CHANNEL(selectedMessageState?.channel))
            dispatch(SET_SHOW_MODAL({ group: true }))
          }
          break
        case 'unread':
          try {
            await dispatch(
              bulkUpdateChatMessages({
                ids:
                  selectedMessageState?.chat_message
                    .filter(
                      (message) =>
                        message.chat_direction === ChatDirection.incoming,
                    )
                    .map((message) => message.chat_message_id)
                    .filter((id): id is string => id !== undefined) ?? [],
                data: { is_read: false },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat marked as unread',
                'Conversation marked as unread.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Mark as unread failed',
                'Marking conversations as unread failed. Please try again.',
              ),
            )
          }
          break
        case 'read':
          try {
            await dispatch(
              bulkUpdateChatMessages({
                ids:
                  selectedMessageState?.chat_message
                    .filter(
                      (message) =>
                        message.chat_direction === ChatDirection.incoming,
                    )
                    .map((message) => message.chat_message_id)
                    .filter((id): id is string => id !== undefined) ?? [],
                data: { is_read: true },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat marked as read',
                'Conversation marked as read.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Mark as read failed',
                'Marking conversations as read failed. Please try again.',
              ),
            )
          }
          break
        case 'block':
          {
            if (participant && participant.length > 0) {
              dispatch(SET_PARTICIPANT(participant[0]))
              dispatch(SET_SHOW_MODAL({ block: true }))
            }
          }
          break
        case 'unblock':
          setShowConfirmUnblockModal(true)
          break
        case 'delete':
          {
            dispatch(SET_CHAT_LIST([selectedMessageState.chat_conversation_id]))
            dispatch(SET_SHOW_MODAL({ deleteChat: true }))
          }
          break
        default:
          {
            const index = contactGroups.findIndex(
              (c) => c.contact_group_id === action,
            )
            if (
              index !== -1 &&
              participant &&
              participant.length === 1 &&
              participant[0].contact?.contact_id
            ) {
              try {
                await dispatch(
                  addContactGroupParticipant({
                    contact_group_id: action,
                    contact_id: participant[0].contact?.contact_id,
                    created_by: '',
                    updated_by: '',
                  }),
                ).unwrap()
                showToast(
                  createSuccessToast(
                    'Added to group',
                    'Contact has been added to the group.',
                  ),
                )
              } catch {
                showToast(
                  createFailedToast(
                    'Add to group failed',
                    'Failed to add contact to group. Please try again.',
                  ),
                )
              }
            }
          }
          break
      }
    }
    if (action !== 'group') setIsKebabBtnActive(false)
  }

  const handleAddToGroup = (args: MenuEventArgs): void => {
    const group = args.item.id
    if (group && selectedMessageState?.chat_conversation_id) {
      if (group === 'new') {
        dispatch(SET_SHOW_MODAL({ group: true }))
      }
      setIsKebabBtnActive(false)
    }
  }

  const generateTimeDisplay = (timestampString: Date | null): string[] => {
    if (timestampString) {
      const timestamp = new Date(timestampString)
      const dateNow = new Date()
      const longDisplay = moment(timestamp).format('MMM D, YYYY - h:mm A')

      if (dateNow.getFullYear() === timestamp.getFullYear()) {
        const dateDifference = dateNow.getDate() - timestamp.getDate()
        if (timestamp.getMonth() == dateNow.getMonth() && dateDifference <= 6) {
          if (
            moment(dateNow).format('MM-DD-YY') ===
            moment(timestamp).format('MM-DD-YY')
          ) {
            const minuteDifference = Math.floor(
              (dateNow.getTime() - timestamp.getTime()) / 60000,
            )
            if (minuteDifference < 1) return ['Just now', longDisplay]
            else if (minuteDifference === 1)
              return [`1 minute ago`, longDisplay]
            else if (minuteDifference < 60)
              return [`${minuteDifference} minutes ago`, longDisplay]
            else if (Math.floor(minuteDifference / 60) === 1)
              return [`1 hour ago`, longDisplay]
            else
              return [
                `${Math.floor(minuteDifference / 60)} hours ago`,
                longDisplay,
              ]
          } else return [moment(timestamp).format('ddd'), longDisplay]
        } else return [moment(timestamp).format('MMM DD'), longDisplay]
      } else return [moment(timestamp).format('MMM DD, YYYY'), longDisplay]
    } else return ['']
  }

  const onSaveEdit = (): void => {
    setIsEditing(false)
  }

  useEffect(() => {
    const clinicId = selectedClinic?.clinic_id
    if (!clinicId) return
    void dispatch(fetchMessagingSettingsByClinic())
  }, [dispatch, selectedClinic?.clinic_id])

  const activeMessagingSettings = messagingSettings

  const isSmsMessagingDisabled =
    selectedMessageState?.channel === 'SMS' &&
    activeMessagingSettings?.enable_sms === false

  const trackMessageStatus =
    activeMessagingSettings?.track_message_status ?? true

  const runAiGeneration = async (
    userText: string,
    featureKey?: string,
    customPrompt?: string,
  ): Promise<void> => {
    const clinicId = selectedClinic?.clinic_id
    if (!clinicId) {
      showToast(
        createFailedToast('AI', 'Select a clinic before using AI features.'),
      )
      setIsGenerating(false)
      setIsLoading(false)
      return
    }

    setIsGenerating(true)
    setIsLoading(true)

    const response = await postAiCompletionApi({
      clinic_id: clinicId,
      user_text: userText,
      feature_key: normalizeAiFeatureKey(featureKey || 'custom'),
      prompt_template: customPrompt,
      user_id: user.user_id,
    })

    if (response.statusCode === 200 && response.data?.content) {
      setAiResponse(response.data.content.replaceAll('\n', '<br/>'))
    } else {
      showToast(
        createFailedToast('AI', response.message || 'AI request failed.'),
      )
    }

    setIsLoading(false)
  }

  const handleSend = (): void => {
    if (selectedMessageState) {
      if (
        selectedMessageState.channel === 'SMS' &&
        activeMessagingSettings?.enable_sms === false
      ) {
        showToast(
          createFailedToast(
            'SMS messaging',
            'SMS messaging is disabled. Enable it in Settings → Communications → Messaging.',
          ),
        )
        return
      }
      if (isAiEnabled) {
        void runAiGeneration(
          editorInput.text,
          aiAction === 'custom' ? 'custom' : aiAction.toLowerCase(),
          aiAction === 'custom' ? inputPrompt : undefined,
        )
        setInputPrompt('')
      } else if (isEditing && !isAiEnabled) {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
        handleConfirmEditChat()
      } else if (isRecording) {
        setIsRecording(false)
        const data: IChatMessage = {
          chat_direction: ChatDirection.outgoing,
          timestamp: new Date().toISOString(),
          content: '🎙️ Audio Recording 🎙️',
          html_content: '🎙️ Audio Recording 🎙️',
          chat_conversation_id:
            selectedMessageState?.chat_conversation_id ?? '',
          sender_id: 'Clinic',
          receiver_id: 'Client',
        }
        dispatch(createChat({ channel: ChatChannel.inApp, ...data }))
      } else {
        let plainText = editorInput.text
        let htmlContent = editorInput.htmlContent
        VariableList.forEach((data) => {
          plainText = plainText.replaceAll(data.text, data.value)
          htmlContent = htmlContent.replaceAll(data.text, data.value)
        })
        const sender_user =
          aiResponse && plainText.includes(aiResponse.replace(/<br\/?>/g, '\n'))
            ? 'AI'
            : 'Clinic'
        if (plainText.length) {
          const newData = {
            chat_direction: ChatDirection.outgoing,
            timestamp: new Date().toISOString(),
            content: plainText,
            html_content: htmlContent,
            chat_conversation_id:
              selectedMessageState?.chat_conversation_id ?? '',
            receiver_id:
              selectedMessageState.chat_participant[0].phone_number ??
              selectedMessageState.chat_participant[0].contact?.cellphone ??
              '',
          }
          if (selectedMessageState?.channel === 'SMS') {
            dispatch(
              createSms({
                ...newData,
                content: plainText,
                calls: undefined,
                send_status: SendStatus.queued,
                sender_id: '',
              }),
            )
          } else {
            dispatch(
              createChat({
                channel: ChatChannel.inApp,
                ...newData,
                sender_id: sender_user,
              }),
            )
          }
          setEditorInput({
            text: '',
            htmlContent: '',
          })
        }
      }
    }
  }

  const onActionBegin = (e: ActionBeginEventArgs): void => {
    if (e.requestType === 'EnterAction') {
      e.cancel = true
      toolbarRef.current?.focusOut()
      handleSend()
    }
  }

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
    }
  }

  const handleToolbar = (buttonName: string): void => {
    const ref = toolbarRef.current

    switch (buttonName) {
      case 'var':
        if (variableRef.current && ref) {
          if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
          variableRef.current.showPopup()
        }
        break
      case 'mention':
        if (mentionRef.current && ref) {
          if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
          mentionRef.current.showPopup()
        }
        break
      case 'ai':
        setShowDefaultToolbar((prevValue) => !prevValue)
        break
      default:
        break
    }
  }

  const handleAIFeaturesButtons = (command: string): void => {
    let newInputPrompt = ''
    switch (command.toLowerCase()) {
      case 'reply':
        newInputPrompt = 'Generate a response for the following message:\n '
        break
      case 'summarize':
        newInputPrompt = `Summarize the message below. Make 
          sure to retain the same context with shorter number of characters.\n `
        break
    }

    if (toolbarRef.current) toolbarRef.current.value = newInputPrompt
    setInputPrompt(newInputPrompt)
  }

  CHAT_LEFT_BUTTONS.map((data) => {
    if (defaultToolbarSettings && defaultToolbarSettings['items']) {
      if (data.type === 'builtin') {
        defaultToolbarSettings['items'].push(data.icon)
      } else if (data.type === 'mgc') {
        defaultToolbarSettings['items'].push({
          template: `<button
           class="e-tbar-btn e-btn custom-toolbar-button"
            tabindex="-1"
          >
            <div class="e-tbar-btn-text" style="font-weight: 500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path id="primary-icon" d="${data.icon}" />
              </svg>
            </div>
          </button>`,
          undo: true,
          click: () => {
            handleToolbar(data.key)
          },
          tooltipText: data.text,
        })
      } else {
        defaultToolbarSettings['items'].push({
          template: `<button class="e-tbar-btn e-btn custom-toolbar-button" tabindex="-1">
          <div class="e-tbar-btn-text" style="font-weight: 500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path id="primary-icon" d="${data.icon}" />
              ${
                Object.keys(data).includes('secondaryIcon')
                  ? `
              <path d="${data.secondaryIcon}" />
              `
                  : ''
              }
            </svg>
          </div>
        </button>`,
          undo: true,
          click: () => {
            handleToolbar(data.key)
          },
          tooltipText: data.text,
        })
      }
    }
  })

  const handleChatToolbarAction = (e: ClickEventArgs): void => {
    const clickedButton = e.item.tooltipText || ''
    const aiButtonClasslist = toolbarRef.current
      ?.getToolbar()
      .querySelector('.ai-feature')?.classList
    if (clickedButton.toLowerCase() === 'ai feature') {
      if (aiButtonClasslist?.contains('custom-toolbar-button-active')) {
        aiButtonClasslist.add('custom-toolbar-button')
        aiButtonClasslist.remove('custom-toolbar-button-active')
      } else if (aiButtonClasslist?.contains('custom-toolbar-button')) {
        aiButtonClasslist.add('custom-toolbar-button-active')
        aiButtonClasslist.remove('custom-toolbar-button')
      }
    }
  }

  const handleInsertAIResponse = (): void => {
    if (toolbarRef.current) toolbarRef.current.value = aiResponse
    setEditorInput({ text: aiResponse, htmlContent: aiResponse })
    setIsGenerating(false)
    setInputPrompt('')
    setShowDefaultToolbar(true)
    setAiAction('custom')
    setIsAiEnabled(false)
    setIsEditing(false)
  }

  const handleAIDropDown = (e: MenuEventArgs): void => {
    const selectedTone = e.item.text?.toLowerCase() || 'custom'
    setAiAction(selectedTone)
    setIsAiEnabled(true)
    void runAiGeneration(editorInput.text, selectedTone)
  }

  const handlePinChat = (): void => {
    setToastText('Message pinned')
    if (chat && chat[lastActiveIndex].chat_message_id) {
      dispatch(
        updateChat({
          chat_message_id: chat[lastActiveIndex].chat_message_id!,
          data: { is_pinned: true },
        }),
      )
    }
    setTimeout(() => setToastText(null), 2000)
  }

  const handleEditChat = (): void => {
    setIsEditing(true)
    if (chat && activeIndex !== -1) {
      setEditorInput({
        text: chat[activeIndex].content,
        htmlContent: chat[activeIndex].content,
      })
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      setIsEditing(false)
      setEditorInput({
        text: '',
        htmlContent: '',
      })
    }, 3600000)
  }

  const handleCancelEditChat = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setIsEditing(false)
    if (toolbarRef.current) toolbarRef.current.value = ''
  }

  const handleCopyChat = (): void => {
    if (chat) {
      const message = extractTextfromHTML(chat[lastActiveIndex].content || '')
      navigator.clipboard.writeText(message)
      setToastText('Text copied to clipboard')

      setTimeout(() => setToastText(null), 1500)
    }
  }

  const handleDeleteChat = async (): Promise<void> => {
    if (!chat || !chat[lastActiveIndex].chat_message_id) return
    try {
      await dispatch(
        updateChat({
          chat_message_id: chat[lastActiveIndex].chat_message_id!,
          data: { is_deleted: true },
        }),
      ).unwrap()
      setTimeout(() => setToastText(null), 2000)
    } catch {
      setToastText(null)
      showToast(
        createFailedToast(
          'Delete failed',
          'Failed to delete message. Please try again.',
        ),
      )
    }
  }

  const handleUndo = (action: string): void => {
    if (chat && selectedMessageState?.channel) {
      const key = action.toLowerCase()
      let data: Partial<IChatMessage> | null = null
      if (key.includes('pin')) {
        data = {
          is_pinned: !chat[lastActiveIndex].is_pinned,
        }
      } else if (key.includes('delete')) {
        data = {
          is_deleted: !chat[lastActiveIndex].is_deleted,
        }
      }
      if (data && chat[lastActiveIndex].chat_message_id) {
        dispatch(
          updateChat({
            chat_message_id: chat[lastActiveIndex].chat_message_id!,
            data,
          }),
        )
      }
    }
    setToastText(null)
  }

  const handleChatMessageAction = (e: MenuEventArgs): void => {
    const action = e.item.text?.toLowerCase() || ''
    switch (action) {
      case 'pin':
        handlePinChat()
        break
      case 'unpin':
        handleUndo('pin')
        break
      case 'edit':
        handleEditChat()
        break
      case 'copy':
        handleCopyChat()
        break
      case 'delete message':
        handleDeleteChat()
        break
      default:
        break
    }
  }

  const scrollToPinnedChat = (): void => {
    if (pinnedChatRef.current) {
      pinnedChatRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleCancelPinnedChat = async (): Promise<void> => {
    if (!pinnedChat || !selectedMessageState?.channel) return
    try {
      await dispatch(
        updateChat({
          chat_message_id: pinnedChat.chat_message_id!,
          data: { is_pinned: false },
        }),
      ).unwrap()
      showToast(createSuccessToast('Pin removed', 'Message has been unpinned.'))
      setToastText(null)
    } catch {
      showToast(
        createFailedToast(
          'Unpin failed',
          'Failed to unpin message. Please try again.',
        ),
      )
    }
  }

  const handleRecordAudio = (): void => {
    setIsRecording(!isRecording)
  }

  const handleRightButtons = (key: string): void => {
    switch (key) {
      case 'send':
        handleSend()
        break
      case 'mic':
        handleRecordAudio()
        break
      default:
        break
    }
  }

  useEffect(() => {
    const reference = toolbarRef.current
    let refElement: Element | undefined = undefined
    if (reference && reference.contentModule) {
      refElement = reference.contentModule.getEditPanel?.()
      if (refElement) {
        refElement.addEventListener('keypress', handleKeyDown as EventListener)
      }
    }
    const interval = setInterval(() => {
      setMessageData((prevMessages) => [...prevMessages])
    }, 60000)

    return () => {
      if (refElement)
        refElement.removeEventListener(
          'keypress',
          handleKeyDown as EventListener,
        )
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    callerInfoRef.current = callerInfo
  }, [callerInfo])

  useEffect(() => {
    currentCallRef.current = currentCall
  }, [currentCall])

  useEffect(() => {
    conversationRef.current = conversations
  }, [conversations])

  useEffect(() => {
    if (!isAiEnabled) {
      setIsGenerating(false)
      setIsLoading(false)
    }
  }, [isAiEnabled])

  // Calculate lastReadMessageIndex when chat changes
  useEffect(() => {
    if (chat && chat.length > 0) {
      let lastReadIndex = -1
      for (let i = chat.length - 1; i >= 0; i--) {
        if (chat[i].is_read) {
          lastReadIndex = i
          break
        }
      }
      setLastReadMessageIndex(lastReadIndex)
    } else {
      setLastReadMessageIndex(-1)
    }
  }, [chat])

  useEffect(() => {
    const routeState = location.state as {
      previousPage?: {
        path?: string
      }
    } | null
    if (routeState?.previousPage?.path) return
    if (lastReadMessageIndex >= 0 && messagesEndRef.current) {
      // Use setTimeout to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [lastReadMessageIndex, location.state])

  useEffect(() => {
    if (selectedMessageState) {
      const tempChat = selectedMessageState.chat_message
      setChat(tempChat)
      setPinnedChat(
        (tempChat?.find(
          (el) => 'is_pinned' in el && el.is_pinned && !el.is_deleted,
        ) as IChatMessage) || null,
      )
      const _p = selectedMessageState.chat_participant
      setParticipant(_p ?? null)
      setIsAiEnabled(false)
    } else {
      setChat(null)
    }
  }, [selectedMessageState, conversations])

  const handleOpenChatDropdown = (index: number): void => {
    setLastActiveIndex(index)
    setActiveIndex(index)
  }

  useEffect(() => {
    setIsBlocked(
      blockedUsers.find(
        (blockedUser) =>
          participant &&
          participant.length === 1 &&
          blockedUser.contact_id === participant[0].contact?.contact_id,
      )
        ? true
        : false,
    )
  }, [blockedUsers, participant])

  useEffect(() => {
    if (toolbarRef.current) toolbarRef.current.value = ''
  }, [selectedMessageState])

  // Scroll to a specific message when deep-linking from patient communication history.
  useEffect(() => {
    const routeState = location.state as {
      chatMessageId?: string
      previousPage?: unknown
    } | null
    const chatMessageId = routeState?.chatMessageId
    if (!chatMessageId) {
      appliedDeepLinkChatMessageIdRef.current = null
      return
    }
    if (appliedDeepLinkChatMessageIdRef.current === chatMessageId) return
    if (!selectedMessageState || chatLoading) return

    const messageIndex =
      chat?.findIndex((m) => m.chat_message_id === chatMessageId) ?? -1

    const previousPage = routeState?.previousPage
    const clearChatMessageDeepLink = (): void => {
      navigate(location.pathname, {
        replace: true,
        state: previousPage ? { previousPage } : null,
      })
    }

    if (!chat || chat.length === 0) return

    if (messageIndex === -1) {
      appliedDeepLinkChatMessageIdRef.current = chatMessageId
      showCommunicationLogNotFoundToast(showToast, 'SMS message')
      clearChatMessageDeepLink()
      return
    }

    appliedDeepLinkChatMessageIdRef.current = chatMessageId

    const timeoutId = setTimeout(() => {
      const container = chatRef.current
      const target = container?.querySelector(
        `[data-chat-message-id="${chatMessageId}"]`,
      )
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setHighlightedMessageId(chatMessageId)
        setTimeout(() => setHighlightedMessageId(null), 1000)
      }
      clearChatMessageDeepLink()
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [
    chat,
    chatLoading,
    location.pathname,
    location.state,
    navigate,
    selectedMessageState,
    showToast,
  ])

  return {
    activeIndex,
    aiAction,
    aiCallSummary,
    aiResponse,
    audioTranscript: TranscriptMockData,
    callerInfo,
    chatInput,
    chatRef,
    chat,
    contactGroups,
    currentCall,
    defaultToolbarSettings,
    editorInput,
    generateTimeDisplay,
    handleAddToGroup,
    handleAIDropDown,
    handleAIFeaturesButtons,
    handleCancelEditChat,
    handleCancelPinnedChat,
    handleMoreOptionsDropdown,
    handleChatMessageAction,
    handleChatToolbarAction,
    handleCopyChat,
    handleInsertAIResponse,
    handleOpenChatDropdown,
    handleUnblockUser,
    handleUndo,
    handleRightButtons,
    handleSend,
    headingButtons: CHAT_HEADING_BUTTONS,
    highlightedMessageId,
    isAiEnabled,
    isBlocked,
    isSmsMessagingDisabled,
    trackMessageStatus,
    isEditing,
    isGenerating,
    isKebabBtnActive,
    isLoading,
    isRecording,
    lastReadMessageIndex,
    makeCall,
    mentionRef,
    messagesEndRef,
    onActionBegin,
    onSaveEdit,
    participant,
    peopleList,
    pinnedChat,
    pinnedChatRef,
    scrollToPinnedChat,
    selectedMessageState,
    setActiveIndex,
    setAiAction,
    setEditorInput,
    setInputPrompt,
    setIsAiEnabled,
    setIsKebabBtnActive,
    setLastActiveIndex,
    setShowConfirmUnblockModal,
    setShowDefaultToolbar,
    setShowSummary,
    setShowTranscriptModal,
    showConfirmUnblockModal,
    showDefaultToolbar,
    showSummary,
    showTranscriptModal,
    toastText,
    toolbarRef,
    variableList: VariableList,
    variableRef,
  }
}

export default useChatPreview
